import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { IntlProvider } from 'react-intl';

import { StencilProvider } from '@amzn/stencil-react-components/context';

import { Col } from '@amzn/stencil-react-components/layout';
import { Flex } from '@amzn/stencil-react-components/layout';

import HeaderContainer from './HeaderContainer';
import FooterContainer from './FooterContainer';
import LeftsideNavigationContainer from './LeftsideNavigationContainer';
import BreadcrumbsContainer from './BreadcrumbsContainer';
import MainContainer from './MainContainer';
import {
  ApplicationContextProvider,
  ApplicationContextConsumer,
} from '../components/context/ApplicationContext';
import WelcomeSlides from '../components/WelcomeSlides/WelcomeSlides';
import LoadingAppLayout from '../components/LoadingAppLayout';

import '../styles/App.css';
import { adjustedTheme } from '../config/theme';
import { IS_FIRST_VISIT } from '../constants/createSegment';
import NoAccessComponent from '../components/common/NoAccessComponent';
import { useCognitoAuth } from '../hooks/useCognitoAuth';
import isEmpty from 'lodash/isEmpty';
import { useUserInfo } from '../hooks/useUserInfo';
import { isUserAuthorized } from '../util/userAuthenticationUtils';
import { ResponseStatus } from '../enums/responseStatus';
import { Spinner, SpinnerSize } from '@amzn/stencil-react-components/spinner';
import ErrorComponent from '../components/common/ErrorComponent';
import ToastNotification from '../components/toast/ToastNotification';

const AppLayout = () => {
  const cognitoAuth = useCognitoAuth();
  const userInfo = useUserInfo();
  const isFirstVisit = Boolean(localStorage.getItem(IS_FIRST_VISIT));
  const [showWelcomeSlides, setShowWelcomeSlides] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(
    () => {
      const { status } = userInfo;
      if (status !== ResponseStatus.PENDING) {
        setIsLoading(false);
        setHasError(false);
      }
      if (status === ResponseStatus.ERROR) {
        setHasError(true);
      }
    },
    [userInfo?.status],
  );

  const body = document.body;
  const html = document.documentElement;

  const bodyHeight =
    Math.max(
      body.scrollHeight,
      body.offsetHeight,
      html.clientHeight,
      html.scrollHeight,
      html.offsetHeight,
    ) - 12;

  const cognitoAuthorizer = cognitoAuth;
  if (!isEmpty(cognitoAuthorizer) && !cognitoAuthorizer?.isUserSignedIn()) {
    return (
      <ApplicationContextProvider>
        <ApplicationContextConsumer>
          {({ contextState }) => {
            <StencilProvider theme={adjustedTheme}>
              <IntlProvider locale={contextState.locale} messages={contextState.messages}>
                <LoadingAppLayout height={bodyHeight} />;
              </IntlProvider>
            </StencilProvider>;
          }}
        </ApplicationContextConsumer>
      </ApplicationContextProvider>
    );
  }
  return (
    <Router>
      <ApplicationContextProvider>
        <ApplicationContextConsumer>
          {({ contextState }) => {
            return (
              <IntlProvider locale={contextState.locale} messages={contextState.messages}>
                <StencilProvider theme={adjustedTheme}>
                  <Flex flexDirection="column" backgroundColor="neutral0" minHeight={bodyHeight}>
                    <HeaderContainer />
                    {isLoading && <Spinner size={SpinnerSize.Large} />}
                    {isUserAuthorized(userInfo) &&
                      !isLoading && (
                        <>
                          <Flex
                            flexDirection="row"
                            gridGap="S400"
                            alignItems="stretch"
                            justifyContent="stretch"
                            flex={1}
                          >
                            <LeftsideNavigationContainer />
                            <Col
                              flex={1}
                              alignItems="stretch"
                              justifyContent="stretch"
                              gridGap="S100"
                              backgroundColor={'neutral0'}
                            >
                              <ToastNotification />
                              <BreadcrumbsContainer />
                              <MainContainer />
                            </Col>
                          </Flex>
                          {!isFirstVisit && (
                            <WelcomeSlides
                              isOpen={showWelcomeSlides}
                              close={() => {
                                localStorage.setItem(IS_FIRST_VISIT, 'true');
                                setShowWelcomeSlides(false);
                              }}
                            />
                          )}
                        </>
                      )}
                    {!isUserAuthorized(userInfo) &&
                      !isLoading && (
                        <Flex
                          flexDirection="row"
                          gridGap="S400"
                          alignItems="stretch"
                          justifyContent="stretch"
                          flex={1}
                        >
                          <Col
                            flex={1}
                            alignItems="stretch"
                            justifyContent="stretch"
                            gridGap="S100"
                            backgroundColor={'neutral0'}
                          >
                            {hasError && <ErrorComponent />}
                            {!hasError && <NoAccessComponent />}
                          </Col>
                        </Flex>
                      )}
                    {!isLoading && <FooterContainer />}
                  </Flex>
                </StencilProvider>
              </IntlProvider>
            );
          }}
        </ApplicationContextConsumer>
      </ApplicationContextProvider>
    </Router>
  );
};

export default AppLayout;
